import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { usePlanEntitlements } from '../../providers/PlanEntitlementsProvider';
import { invokeEdgeOrPresent } from '../../../shared/supabase/invokeEdgeOrPresent';
import { getSupabaseConfig, supabase } from '../../../shared/supabase/supabaseClient';
import {
  activePlanGrantBannerText,
  parseActivePlanGrant,
  planPriceArs,
} from '../../../../../../shared/billing-catalog';
import { usePublicBillingCatalog } from './usePublicBillingCatalog';
import {
  clearPlanIntent,
  clearPlanIntentIfSubscribed,
  persistPlanIntent,
  resolvePendingPlanIntent,
  type PaidPlanCode,
} from '../../feature-flags/planIntentService';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { resolveSubscribedPaidTier } from '../billingScreen.utils';
import { resolveBillingReminderState } from '../billingReminderState';
import { resolveBillingAccessState } from '../billingAccessMode';
import { verifyMpSubscription } from '../verifyMpSubscription';

function readMpReturnParam(): 'success' | 'pending' | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  const mp = new URLSearchParams(window.location.search).get('mp');
  if (mp === 'success' || mp === 'pending') return mp;
  return null;
}

/** MP anexa `preapproval_id` al back_url al volver del checkout hosteado. */
function readMpReturnPreapprovalId(): string | undefined {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
  const id = new URLSearchParams(window.location.search).get('preapproval_id');
  return id && /^[a-zA-Z0-9]+$/.test(id) ? id : undefined;
}

function clearMpReturnParamFromUrl(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('mp')) return;
  url.searchParams.delete('mp');
  url.searchParams.delete('preapproval_id');
  const qs = url.searchParams.toString();
  const next = `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`;
  window.history.replaceState({}, '', next);
}

/**
 * Confirma el checkout pendiente prefetchado (`confirm_only`) durante el redirect.
 * `keepalive` para que el request aterrice aunque la página navegue a MP.
 * Best-effort: si no llega, el webhook + verify activan el plan igual.
 */
async function confirmMpCheckout(planCode: PaidPlanCode, orgId: string, preapprovalId: string): Promise<void> {
  try {
    const { url, anonKey } = getSupabaseConfig();
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    await fetch(`${url}/functions/v1/mercadopago-subscription-create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        plan_code: planCode,
        organization_id: orgId,
        confirm_only: true,
        preapproval_id: preapprovalId,
      }),
      keepalive: true,
    });
  } catch {
    /* best effort */
  }
}

type PrefetchedCheckout = { init_point: string; preapproval_id: string | null };

/** Clave del prefetch: plan + e-mail pagador declarado (un cambio de e-mail invalida el init_point). */
const prefetchKey = (planCode: PaidPlanCode, payerEmail: string) => `${planCode}|${payerEmail}`;

export function useBillingWebMercadoPago() {
  const { entitlements, loading, refresh: refreshEntitlements } = usePlanEntitlements();
  const { catalog, loading: catalogLoading } = usePublicBillingCatalog();
  const sessionUser = useAuthStore((s) => s.session?.user);
  const [subError, setSubError] = useState<string | undefined>();
  const [subscribing, setSubscribing] = useState<PaidPlanCode | null>(null);
  const [mpBanner, setMpBanner] = useState<string | undefined>();
  const [payerEmailOverride, setPayerEmailOverride] = useState<string | null>(null);
  const prefetchedRef = useRef<Record<string, PrefetchedCheckout | Promise<PrefetchedCheckout | null> | null>>({});

  /** e-mail de la cuenta MP que pagará (override del usuario o su e-mail Vuelto). MP lo valida en el checkout. */
  const payerEmail = (payerEmailOverride ?? sessionUser?.email ?? '').trim().toLowerCase();

  const setPayerEmail = useCallback((email: string | null) => {
    setPayerEmailOverride(email ? email.trim().toLowerCase() : null);
  }, []);

  const emprendePrice = useMemo(() => planPriceArs(catalog, 'emprende'), [catalog]);
  const crecePrice = useMemo(() => planPriceArs(catalog, 'crece'), [catalog]);

  const subscribedTier = useMemo(() => resolveSubscribedPaidTier(entitlements), [entitlements]);

  const activePlanGrant = useMemo(
    () => parseActivePlanGrant(entitlements?.active_plan_grant),
    [entitlements?.active_plan_grant],
  );

  const pendingPlan = useMemo(
    () => resolvePendingPlanIntent(entitlements, sessionUser?.user_metadata?.pending_plan as string | undefined),
    [entitlements, sessionUser?.user_metadata?.pending_plan],
  );

  const billingReminders = useMemo(
    () =>
      resolveBillingReminderState({
        entitlements,
        subscribedTier,
        pendingPlan,
      }),
    [entitlements, subscribedTier, pendingPlan],
  );

  const access = useMemo(
    () => resolveBillingAccessState({ entitlements, pendingPlan }),
    [entitlements, pendingPlan],
  );

  const grantBanner = activePlanGrantBannerText(activePlanGrant);

  useEffect(() => {
    if (!entitlements) return;
    void clearPlanIntentIfSubscribed(entitlements);
  }, [entitlements]);

  useEffect(() => {
    const mp = readMpReturnParam();
    if (!mp) return;

    setMpBanner(
      mp === 'success'
        ? 'Sincronizando tu plan…'
        : 'Mercado Pago aún no confirmó el pago.',
    );
    const orgId = (entitlements?.organization_id as string | undefined) ?? null;
    const preapprovalId = readMpReturnPreapprovalId();
    void verifyMpSubscription(orgId, preapprovalId).finally(() => {
      void refreshEntitlements({ force: true });
    });
    clearMpReturnParamFromUrl();
  }, [entitlements?.organization_id, refreshEntitlements]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !pendingPlan || subscribedTier !== 'gratis' || activePlanGrant) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let attempt = 0;
    const delays = [4000, 8000, 16000, 32000, 60000];

    const poll = async () => {
      if (cancelled) return;
      // Verify activo: si MP ya autorizó y el webhook no llegó, esto activa igual.
      // Sin org_id: el server resuelve por pending_subscription_plan.
      await verifyMpSubscription(null);
      if (cancelled) return;
      await refreshEntitlements({ force: true });
      attempt += 1;
      if (cancelled) return;
      if (attempt >= delays.length) {
        setMpBanner(undefined);
        return;
      }
      timer = window.setTimeout(poll, delays[attempt]);
    };

    timer = window.setTimeout(poll, delays[0]);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [activePlanGrant, pendingPlan, refreshEntitlements, sessionUser?.user_metadata?.pending_plan, subscribedTier]);

  // Pre-warm del isolate de la Edge Function (OPTIONS barato): el click no paga cold start.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const base = process.env.EXPO_PUBLIC_SUPABASE_URL;
    if (!base) return;
    void fetch(`${base}/functions/v1/mercadopago-subscription-create`, { method: 'OPTIONS' }).catch(
      () => {},
    );
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        void refreshEntitlements();
        debounce = null;
      }, 500);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (debounce) clearTimeout(debounce);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshEntitlements]);

  /**
   * Prefetch silencioso del checkout pendiente: crea el preapproval `pending`
   * (defer_pending, sin writes durables) al entrar a la pantalla. El click real
   * consume el init_point → redirect casi instantáneo.
   */
  const prefetchMpCheckout = useCallback(
    (planCode: PaidPlanCode) => {
      if (Platform.OS !== 'web') return;
      const rowNow = entitlements;
      if (rowNow?.is_owner !== true || !rowNow.organization_id) return;
      if (resolveSubscribedPaidTier(rowNow) === planCode) return;
      const key = prefetchKey(planCode, payerEmail);
      if (prefetchedRef.current[key]) return;

      const task = (async (): Promise<PrefetchedCheckout | null> => {
        try {
          const { data } = await supabase.functions.invoke('mercadopago-subscription-create', {
            body: {
              plan_code: planCode,
              organization_id: rowNow.organization_id,
              payer_email: payerEmail || undefined,
              defer_pending: true,
            },
          });
          const json = (data ?? {}) as { ok?: boolean; init_point?: string; preapproval_id?: string };
          if (!json.ok || typeof json.init_point !== 'string' || !json.init_point) return null;
          return { init_point: json.init_point, preapproval_id: json.preapproval_id ?? null };
        } catch {
          return null;
        }
      })();

      prefetchedRef.current[key] = task;
      void task
        .then((result) => {
          if (prefetchedRef.current[key] === task) {
            prefetchedRef.current[key] = result ?? null;
          }
        })
        .catch(() => {
          if (prefetchedRef.current[key] === task) prefetchedRef.current[key] = null;
        });
    },
    [entitlements, payerEmail],
  );

  /**
   * Click en "Continuar con Mercado Pago": consume el prefetch o crea el
   * preapproval en el momento, confirma la intención (keepalive + RPC) y
   * redirige al init_point de MP. La activación queda en webhook + verify.
   */
  const startMpCheckout = useCallback(
    async (planCode: PaidPlanCode): Promise<boolean> => {
      if (Platform.OS !== 'web' || typeof window === 'undefined') {
        setSubError('El pago con Mercado Pago se hace desde la web.');
        return false;
      }
      setSubError(undefined);
      const rowNow = entitlements;
      if (rowNow?.is_owner !== true) {
        setSubError('Solo el dueño del negocio puede cambiar el plan.');
        return false;
      }
      if (resolveSubscribedPaidTier(rowNow) === planCode) {
        setSubError(`Ya tenés el plan ${planCode === 'emprende' ? 'Emprende' : 'Crece'} activo.`);
        return false;
      }
      const organizationId = rowNow.organization_id;
      if (!organizationId) {
        setSubError('No se pudo resolver tu negocio. Reintentá en un momento.');
        return false;
      }

      setSubscribing(planCode);
      try {
        let checkout: PrefetchedCheckout | null = null;
        const cached = prefetchedRef.current[prefetchKey(planCode, payerEmail)];
        if (cached) {
          checkout = await Promise.resolve(cached).catch(() => null);
        }
        if (!checkout) {
          const { data, error: invokeError, presented, kind } = await invokeEdgeOrPresent(
            'mercadopago-subscription-create',
            {
              body: {
                plan_code: planCode,
                organization_id: organizationId,
                payer_email: payerEmail || undefined,
              },
            },
            {
              module: 'billing',
              op: 'billing.mp_subscription_create',
              fallbackMessage: 'No se pudo iniciar el checkout. Intentá de nuevo.',
            },
          );
          if (invokeError) {
            if (presented) return false;
            setSubError(kind === 'offline' ? 'Error de red. Intentá de nuevo.' : invokeError.message);
            return false;
          }
          const json = (data ?? {}) as { ok?: boolean; init_point?: string; preapproval_id?: string; error?: string };
          if (!json.ok || typeof json.init_point !== 'string' || !json.init_point) {
            setSubError(json.error ?? 'No se pudo iniciar el checkout. Intentá de nuevo.');
            return false;
          }
          checkout = { init_point: json.init_point, preapproval_id: json.preapproval_id ?? null };
        }
        delete prefetchedRef.current[prefetchKey(planCode, payerEmail)];

        void persistPlanIntent(planCode);
        if (checkout.preapproval_id) {
          void confirmMpCheckout(planCode, organizationId, checkout.preapproval_id);
        }
        window.location.assign(checkout.init_point);
        return true;
      } catch {
        setSubError('Error de red. Intentá de nuevo.');
        return false;
      } finally {
        setSubscribing(null);
      }
    },
    [entitlements, payerEmail],
  );

  const dismissPendingPlan = useCallback(async () => {
    await clearPlanIntent();
    await refreshEntitlements();
  }, [refreshEntitlements]);

  return {
    entitlements,
    loading: loading || catalogLoading,
    refresh: refreshEntitlements,
    catalog,
    payerEmail,
    defaultPayerEmail: (sessionUser?.email ?? '').trim().toLowerCase(),
    setPayerEmail,
    subscribedTier,
    activePlanGrant,
    access,
    grantBanner,
    signupTrialReminder: billingReminders.signupTrialReminder,
    subscriptionLapsedReminder: billingReminders.subscriptionLapsedReminder,
    pendingPlan,
    highlightedPlan: billingReminders.highlightedPlan,
    subError,
    subscribing,
    mpBanner,
    emprendePrice,
    crecePrice,
    prefetchMpCheckout,
    startMpCheckout,
    dismissPendingPlan,
  };
}
